import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import AssetProfileFormFields from '@/components/assets/AssetProfileFormFields';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';
import {
  attachPortfolioLinks,
  attachCompanyLinks,
  handleCompanySelection,
  handlePortfolioSelection,
  handleSubPortfolioSelection,
  handleTechnologyChange,
  mapAssetToForm,
  prepareAssetPayload,
  validateAssetForm,
} from '@/lib/assets';
import {
  ArrowLeft,
  Wind,
  Sun,
  Battery,
  Activity,
  Thermometer,
  Gauge,
  Zap,
  TrendingUp,
  AlertTriangle,
  Wrench,
  FileText,
  DollarSign,
  MapPin,
  RefreshCw,
  Trash2,
  Edit2,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const assetIcons = {
  wind: Wind,
  solar: Sun,
  battery: Battery,
};

const statusColors = {
  operational: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  warning: 'bg-amber-100 text-amber-700 border-amber-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
  offline: 'bg-slate-100 text-slate-600 border-slate-300',
};

const lifecycleStatusColors = {
  operational: 'bg-sky-100 text-sky-700 border-sky-300',
  cod_achieved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  performance_testing: 'bg-violet-100 text-violet-700 border-violet-300',
  grid_connection_in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  under_construction: 'bg-orange-100 text-orange-700 border-orange-300',
  under_development: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  planned_initiative: 'bg-slate-100 text-slate-600 border-slate-300',
};

const internalRoles = ['superadmin', 'platform_admin', 'org_admin', 'org_user'];

function formatLabel(value) {
  if (!value) return 'Not set';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function displayValue(value) {
  return value || 'Not set';
}

export default function AssetDetailPage() {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [subPortfolios, setSubPortfolios] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sensorData, setSensorData] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchAssetData = useCallback(async () => {
    try {
      const [
        assetRes,
        usersRes,
        portfoliosRes,
        subPortfoliosRes,
        companiesRes,
        sensorRes,
        perfRes,
      ] = await Promise.all([
        axios.get(`${API}/assets/${id}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/portfolios`),
        axios.get(`${API}/sub-portfolios`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/assets/${id}/sensor-data`),
        axios.get(`${API}/assets/${id}/performance`),
      ]);
      const normalizedAsset = attachCompanyLinks(
        attachPortfolioLinks(assetRes.data, portfoliosRes.data, subPortfoliosRes.data),
        companiesRes.data
      );
      setAsset(normalizedAsset);
      setEditForm(mapAssetToForm(normalizedAsset));
      setInternalUsers(
        usersRes.data.filter((user) => internalRoles.includes(user.role) && user.status === 'active')
      );
      setPortfolios(portfoliosRes.data);
      setSubPortfolios(subPortfoliosRes.data);
      setCompanies(companiesRes.data);
      setSensorData(sensorRes.data);
      setPerformance(perfRes.data);
    } catch (error) {
      console.error('Failed to fetch asset data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSensorData = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/assets/${id}/sensor-data`);
      setSensorData(response.data);
    } catch (error) {
      console.error('Failed to fetch sensor data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAssetData();
    const interval = setInterval(() => {
      fetchSensorData();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchAssetData, fetchSensorData]);

  const handleEditChange = (key, value) => {
    setEditForm((state) => ({ ...state, [key]: value }));
  };

  const handleEditTechnologyChange = (technology) => {
    setEditForm((state) => handleTechnologyChange(state, technology));
  };

  const handleEditAssetManagerChange = (userId) => {
    const selectedUser = internalUsers.find((user) => user.id === userId);
    setEditForm((state) => ({
      ...state,
      asset_manager_id: userId,
      asset_manager_name: selectedUser?.name || '',
    }));
  };

  const handleEditPortfolioChange = (portfolioId) => {
    setEditForm((state) => handlePortfolioSelection(state, portfolioId, portfolios, subPortfolios));
  };

  const handleEditSubPortfolioChange = (subPortfolioId) => {
    setEditForm((state) => handleSubPortfolioSelection(state, subPortfolioId, subPortfolios));
  };

  const handleEditCompanyChange = (sectionKey, companyId) => {
    setEditForm((state) => handleCompanySelection(state, sectionKey, companyId, companies));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!asset) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Asset not found</p>
          <Button asChild className="mt-4">
            <Link to="/assets">Back to Assets</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const Icon = assetIcons[asset.type] || Activity;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/assets" data-testid="back-to-assets">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div
              className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center',
                asset.type === 'wind' ? 'bg-sky-100' : asset.type === 'solar' ? 'bg-amber-100' : 'bg-emerald-100'
              )}
            >
              <Icon
                className={cn(
                  'w-6 h-6',
                  asset.type === 'wind' ? 'text-sky-600' : asset.type === 'solar' ? 'text-amber-600' : 'text-emerald-600'
                )}
              />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl font-bold text-slate-900">{asset.name}</h1>
                <Badge variant="outline" className={cn(statusColors[asset.operational_status])}>
                  {asset.operational_status.toUpperCase()}
                </Badge>
                <Badge variant="outline" className={cn(lifecycleStatusColors[asset.lifecycle_status])}>
                  {formatLabel(asset.lifecycle_status)}
                </Badge>
              </div>
              <p className="text-slate-500 flex items-center gap-2">
                <span className="font-mono">{asset.asset_id}</span>
                <span>•</span>
                <MapPin className="w-4 h-4" />
                {asset.sub_portfolio}
                <span>•</span>
                {asset.portfolio}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchSensorData}
              disabled={refreshing}
              data-testid="refresh-sensor"
            >
              <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button variant="default" size="sm" data-testid="create-workorder">
              <Wrench className="w-4 h-4 mr-2" />
              Create Work Order
            </Button>
            {!editing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={async () => {
                  if (!window.confirm('Delete this asset?')) return;
                  try {
                    await axios.delete(`${API}/assets/${id}`);
                    navigate('/assets');
                  } catch (err) {
                    console.error('Failed to delete asset', err);
                    toast.error('Failed to delete asset');
                  }
                }}>
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </Button>
              </>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    const missingFields = validateAssetForm(editForm);
                    if (missingFields.length > 0) {
                      toast.error(`Please complete: ${missingFields.join(', ')}`);
                      return;
                    }

                    try {
                      const payload = prepareAssetPayload(editForm, internalUsers);
                      await axios.patch(`${API}/assets/${id}`, payload);
                      setEditing(false);
                      fetchAssetData();
                    } catch (err) {
                      console.error('Failed to update asset', err);
                      toast.error('Failed to update asset');
                    }
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setEditForm(mapAssetToForm(asset));
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>

        {editing && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <AssetProfileFormFields
                form={editForm}
                users={internalUsers}
                portfolios={portfolios}
                subPortfolios={subPortfolios}
                companies={companies}
                onChange={handleEditChange}
                onTechnologyChange={handleEditTechnologyChange}
                onAssetManagerChange={handleEditAssetManagerChange}
                onPortfolioChange={handleEditPortfolioChange}
                onSubPortfolioChange={handleEditSubPortfolioChange}
                onCompanyChange={handleEditCompanyChange}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="border-slate-200 xl:col-span-2">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Commercial Profile</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Portfolio</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.portfolio)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Sub-portfolio</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.sub_portfolio)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Technology</p>
                  <p className="font-medium text-slate-900">{displayValue(formatLabel(asset.type))}</p>
                </div>
                <div>
                  <p className="text-slate-500">Asset Type</p>
                  <p className="font-medium text-slate-900">{displayValue(formatLabel(asset.asset_type))}</p>
                </div>
                <div>
                  <p className="text-slate-500">DC Installed Capacity</p>
                  <p className="font-medium text-slate-900">
                    {asset.dc_installed_capacity_kwp
                      ? `${Number(asset.dc_installed_capacity_kwp).toLocaleString()} kWp`
                      : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Export Capacity</p>
                  <p className="font-medium text-slate-900">
                    {asset.export_capacity_kva ? `${asset.export_capacity_kva} kVA` : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Import Capacity</p>
                  <p className="font-medium text-slate-900">
                    {asset.import_capacity_kva ? `${asset.import_capacity_kva} kVA` : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Asset Manager</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.asset_manager_name)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Commercial Operation</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.commercial_operation_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Address</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-3 text-sm">
              <div>
                <p className="text-slate-500">Address Line</p>
                <p className="font-medium text-slate-900">{displayValue(asset.address_line)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-slate-500">City</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.city)}</p>
                </div>
                <div>
                  <p className="text-slate-500">State / Region</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.state_region)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Country</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.country)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Pincode</p>
                  <p className="font-medium text-slate-900">{displayValue(asset.pincode)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Company Contacts</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {[
                ['SPV', 'spv'],
                ['Holding Company', 'holding_company'],
                ['O&M Company', 'om_company'],
                ['EPC Company', 'epc_company'],
                ['Lender', 'lender'],
              ].map(([label, prefix]) => (
                <div key={prefix} className="rounded-lg border border-slate-200 p-4">
                  <p className="font-medium text-slate-900">{label}</p>
                  <p className="text-sm text-slate-500 mt-1">{displayValue(asset[`${prefix}_name`])}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                    <div>
                      <p className="text-slate-500">Contact Person</p>
                      <p className="font-medium text-slate-900">
                        {displayValue(asset[`${prefix}_contact_person`])}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">
                        {displayValue(asset[`${prefix}_contact_email`])}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium text-slate-900">
                        {displayValue(asset[`${prefix}_contact_phone`])}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Key Dates</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {[
                  ['Grid connection date', asset.grid_connection_date],
                  ['Commissioning', asset.commissioning_date],
                  ['Commercial operation', asset.commercial_operation_date],
                  ['Management start date', asset.management_start_date],
                  ['Management end date', asset.management_end_date],
                  ['Contract renewal date', asset.contract_renewal_date],
                  ['Sold date', asset.sold_date],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-slate-500">{label}</p>
                    <p className="font-medium text-slate-900">{displayValue(value)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200" data-testid="kpi-availability">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Availability</span>
                <Activity className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
                {formatPercent(asset.availability)}
              </p>
              <Progress value={asset.availability} className="h-1.5 mt-2" />
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="kpi-health">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Health Score</span>
                <Gauge className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900 mt-1">{asset.health_score}%</p>
              <Progress
                value={asset.health_score}
                className={cn('h-1.5 mt-2', asset.health_score < 70 && '[&>div]:bg-red-500')}
              />
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="kpi-performance">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Performance Ratio</span>
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
                {formatPercent(asset.performance_ratio)}
              </p>
              <p className="text-xs text-emerald-600 mt-1">+1.2% vs fleet avg</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="kpi-revenue">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Revenue MTD</span>
                <DollarSign className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-heading font-bold text-emerald-600 mt-1">
                {formatCurrency(asset.revenue_mtd, true)}
              </p>
              <p className="text-xs text-slate-500 mt-1">Next maint: {asset.next_maintenance}</p>
            </CardContent>
          </Card>
        </div>

        {/* Live Sensor Data */}
        <Card className="border-slate-200" data-testid="live-sensor-data">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              Live Sensor Data
              <div className="flex items-center gap-1 text-sm font-normal text-slate-500">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Real-time
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm">Power Output</span>
                </div>
                <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.power_output || 0} MW</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Thermometer className="w-4 h-4" />
                  <span className="text-sm">Temperature</span>
                </div>
                <p
                  className={cn(
                    'font-mono text-xl font-bold',
                    sensorData?.temperature > 80 ? 'text-red-600' : 'text-slate-900'
                  )}
                >
                  {sensorData?.temperature || 0}°C
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Activity className="w-4 h-4" />
                  <span className="text-sm">Efficiency</span>
                </div>
                <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.efficiency || 0}%</p>
              </div>

              {asset.type === 'wind' && (
                <>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Wind className="w-4 h-4" />
                      <span className="text-sm">Wind Speed</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.wind_speed || 0} m/s</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Gauge className="w-4 h-4" />
                      <span className="text-sm">Rotor Speed</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.rotor_speed || 0} rpm</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Thermometer className="w-4 h-4" />
                      <span className="text-sm">Gearbox Temp</span>
                    </div>
                    <p
                      className={cn(
                        'font-mono text-xl font-bold',
                        sensorData?.gearbox_temp > 90 ? 'text-red-600' : 'text-slate-900'
                      )}
                    >
                      {sensorData?.gearbox_temp || 0}°C
                    </p>
                  </div>
                </>
              )}

              {asset.type === 'solar' && (
                <>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Sun className="w-4 h-4" />
                      <span className="text-sm">Irradiance</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-900">
                      {sensorData?.solar_irradiance || 0} W/m²
                    </p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Thermometer className="w-4 h-4" />
                      <span className="text-sm">Panel Temp</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.panel_temp || 0}°C</p>
                  </div>
                </>
              )}

              {asset.type === 'battery' && (
                <>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Battery className="w-4 h-4" />
                      <span className="text-sm">State of Charge</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-900">{sensorData?.battery_soc || 0}%</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Zap className="w-4 h-4" />
                      <span className="text-sm">Charge Rate</span>
                    </div>
                    <p
                      className={cn(
                        'font-mono text-xl font-bold',
                        sensorData?.charge_rate > 0 ? 'text-emerald-600' : 'text-amber-600'
                      )}
                    >
                      {sensorData?.charge_rate > 0 ? '+' : ''}
                      {sensorData?.charge_rate || 0} kW
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="performance" className="space-y-4">
          <TabsList>
            <TabsTrigger value="performance" data-testid="tab-performance">
              Performance
            </TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance">
              Maintenance
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              Alerts
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="performance">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Availability Chart */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="font-heading text-lg">7-Day Availability</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={performance}>
                      <defs>
                        <linearGradient id="colorAvail" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis domain={[85, 100]} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip />
                      <Area type="monotone" dataKey="availability" stroke="#0ea5e9" fillOpacity={1} fill="url(#colorAvail)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Revenue Chart */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="font-heading text-lg">Daily Revenue</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={performance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => `$${v / 1000}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="maintenance">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Maintenance History</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {[
                    { date: '2026-01-15', type: 'Scheduled', title: 'Quarterly Inspection', status: 'Completed' },
                    { date: '2025-12-20', type: 'Corrective', title: 'Bearing Replacement', status: 'Completed' },
                    { date: '2025-11-10', type: 'Scheduled', title: 'Lubrication Service', status: 'Completed' },
                  ].map((maint, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Wrench className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{maint.title}</p>
                        <p className="text-sm text-slate-500">
                          {maint.type} • {maint.date}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                        {maint.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Active Alerts</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {asset.operational_status === 'critical' ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">Gearbox Temperature Critical</p>
                        <p className="text-sm text-red-700 mt-1">
                          Temperature exceeding threshold (95°C). Immediate inspection required.
                        </p>
                        <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                          <p className="text-xs font-medium text-sky-800">AI Recommendation:</p>
                          <p className="text-sm text-sky-700 mt-1">
                            Schedule immediate inspection. Consider temporary deration to 80% capacity to prevent further
                            damage.
                          </p>
                        </div>
                        <Button size="sm" className="mt-3" data-testid="alert-create-wo">
                          Create Work Order
                        </Button>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">-$12,500</p>
                        <p className="text-xs text-red-500">per day</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No active alerts for this asset</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Documents</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {[
                    { name: 'Technical Manual.pdf', size: '2.4 MB', date: '2025-06-15' },
                    { name: 'Maintenance Schedule.xlsx', size: '156 KB', date: '2025-12-01' },
                    { name: 'Warranty Certificate.pdf', size: '890 KB', date: '2024-03-20' },
                  ].map((doc, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                        <p className="text-xs text-slate-500">{doc.size}</p>
                      </div>
                      <span className="text-xs text-slate-500">{doc.date}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
